import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Typography,
    Button,
    Box,
    Card,
    CardContent,
    CardMedia,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Alert,
    Snackbar,
    CardActions,
    CircularProgress,
    Chip,
    OutlinedInput,
    Checkbox,
    ListItemText,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Image as ImageIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';

import { API_BASE_URL as API_URL } from '../config';
import { GymService } from '../services/gymService';
import type { Gym } from '../services/types';

interface Equipment {
    id?: number;
    name: string;
    type: string;
    description: string;
    image: string;
    make?: string;
    initialResistance?: string;
    muscleGroups: string[];
    gym?: any; // Gym object or IRI string
    createdAt?: string;
}

const equipmentTypes = [
    { value: 'free_weight', label: 'Свободна тежест' },
    { value: 'machine', label: 'Машина' },
    { value: 'mat', label: 'Стелка' },
    { value: 'box', label: 'Бокс' },
    { value: 'other', label: 'Друго' },
];

const muscleGroupOptions = [
    { value: 'chest', label: 'Гръдни' },
    { value: 'back', label: 'Гръб' },
    { value: 'quadriceps', label: 'Квадрицепс' },
    { value: 'hamstrings', label: 'Задно бедро' },
    { value: 'adductors', label: 'Аддуктори' },
    { value: 'shoulders', label: 'Рамене' },
    { value: 'arms', label: 'Ръце' },
    { value: 'core', label: 'Корем' },
    { value: 'glutes', label: 'Глутеуси' },
    { value: 'calves', label: 'Икри' },
];

const getMuscleGroupLabel = (value: string) => {
    return muscleGroupOptions.find(m => m.value === value)?.label || value;
};

const muscleGroupColors: Record<string, string> = {
    chest: '#e53935',
    back: '#1e88e5',
    quadriceps: '#43a047',
    hamstrings: '#2e7d32',
    adductors: '#7cb342',
    shoulders: '#fb8c00',
    arms: '#8e24aa',
    core: '#00acc1',
    glutes: '#d81b60',
    calves: '#6d4c41',
};

const EquipmentManager: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [equipment, setEquipment] = useState<Equipment[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null);
    const [formData, setFormData] = useState<Equipment>({
        name: '',
        type: 'free_weight',
        description: '',
        image: '',
        make: '',
        initialResistance: '',
        muscleGroups: [],
    });
    const [selectedGymId, setSelectedGymId] = useState<number | ''>('');
    const [gyms, setGyms] = useState<Gym[]>([]);
    const [filterGymId, setFilterGymId] = useState<number | ''>('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>('');
    const [uploading, setUploading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);

    useEffect(() => {
        fetchEquipment();
        fetchGyms();
    }, []);

    useEffect(() => {
        const editIdParam = searchParams.get('edit');
        if (!editIdParam || fetchLoading) {
            return;
        }

        const equipmentId = Number(editIdParam);
        if (Number.isNaN(equipmentId)) {
            removeEditQueryParam();
            return;
        }

        const targetEquipment = equipment.find((eq) => eq.id === equipmentId);
        if (targetEquipment) {
            handleOpenDialog(targetEquipment);
        } else {
            showSnackbar('Оборудването не е намерено', 'error');
        }

        removeEditQueryParam();
    }, [searchParams, fetchLoading, equipment]);

    const fetchGyms = async () => {
        try {
            const data = await GymService.getAll();
            setGyms(data);
        } catch (error) {
            console.error('Error fetching gyms:', error);
        }
    };

    const fetchEquipment = async () => {
        setFetchLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/equipment`, {
                withCredentials: true,
                headers: {
                    'Accept': 'application/ld+json',
                },
            });
            // Handle both 'member' and 'hydra:member' response formats
            const equipmentData = response.data['hydra:member'] || response.data.member || [];
            console.log('Fetched equipment:', equipmentData);
            console.log('Equipment count:', equipmentData.length);
            setEquipment(equipmentData);
        } catch (error) {
            console.error('Error fetching equipment:', error);
            showSnackbar('Грешка при зареждане на оборудването', 'error');
        } finally {
            setFetchLoading(false);
        }
    };

    const removeEditQueryParam = () => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('edit');
        setSearchParams(nextParams, { replace: true });
    };

    const getGymFromEquipment = (eq: Equipment): number | '' => {
        if (!eq.gym) return '';
        if (typeof eq.gym === 'object' && eq.gym.id) return eq.gym.id;
        if (typeof eq.gym === 'string') {
            const match = eq.gym.match(/\/api\/gyms\/(\d+)/);
            return match ? parseInt(match[1]) : '';
        }
        return '';
    };

    const getGymName = (eq: Equipment): string | null => {
        if (!eq.gym) return null;
        if (typeof eq.gym === 'object' && eq.gym.name) return eq.gym.name;
        if (typeof eq.gym === 'string') {
            const match = eq.gym.match(/\/api\/gyms\/(\d+)/);
            if (match) {
                const gym = gyms.find(g => g.id === parseInt(match[1]));
                return gym?.name || null;
            }
        }
        return null;
    };

    const handleOpenDialog = (eq?: Equipment) => {
        if (eq) {
            setEditingEquipment(eq);
            setFormData({
                ...eq,
                make: eq.make || '',
                initialResistance: eq.initialResistance != null ? String(eq.initialResistance) : '',
                muscleGroups: eq.muscleGroups || [],
            });
            setSelectedGymId(getGymFromEquipment(eq));
            setImagePreview(eq.image || '');
        } else {
            setEditingEquipment(null);
            setFormData({
                name: '',
                type: 'free_weight',
                description: '',
                image: '',
                make: '',
                initialResistance: '',
                muscleGroups: [],
            });
            setSelectedGymId('');
            setImagePreview('');
        }
        setImageFile(null);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingEquipment(null);
        setFormData({
            name: '',
            type: 'free_weight',
            description: '',
            image: '',
            make: '',
            initialResistance: '',
            muscleGroups: [],
        });
        setSelectedGymId('');
        setImageFile(null);
        setImagePreview('');
    };

    const handleInputChange = (field: keyof Equipment, value: string) => {
        setFormData({ ...formData, [field]: value });
    };

    const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Check file size (30MB = 31457280 bytes)
            if (file.size > 31457280) {
                showSnackbar('Файлът е твърде голям. Максималният размер е 30MB.', 'error');
                return;
            }

            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadImage = async (): Promise<string> => {
        if (!imageFile) return formData.image || '';

        setUploading(true);
        const formDataToSend = new FormData();
        formDataToSend.append('image', imageFile);

        try {
            const response = await axios.post(`${API_URL}/api/upload/equipment-image`, formDataToSend, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data.url;
        } catch (error: any) {
            console.error('Error uploading image:', error);
            throw new Error(error.response?.data?.error || 'Грешка при качване на изображението');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            showSnackbar('Моля, въведете име', 'error');
            return;
        }

        setLoading(true);
        try {
            let imageUrl = formData.image;
            if (imageFile) {
                imageUrl = await uploadImage();
            }

            const dataToSend: any = {
                ...formData,
                image: imageUrl,
                make: formData.make?.trim() || null,
                initialResistance: formData.type === 'machine' && formData.initialResistance !== '' && formData.initialResistance != null
                    ? String(parseFloat(formData.initialResistance))
                    : null,
                muscleGroups: formData.muscleGroups.length > 0 ? formData.muscleGroups : null,
                gym: selectedGymId ? `/api/gyms/${selectedGymId}` : null,
            };
            delete dataToSend.createdAt;

            if (editingEquipment) {
                // Update
                await axios.patch(`${API_URL}/api/equipment/${editingEquipment.id}`, dataToSend, {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/merge-patch+json',
                    },
                });
                showSnackbar('Оборудването е актуализирано успешно', 'success');
            } else {
                // Create
                await axios.post(`${API_URL}/api/equipment`, dataToSend, {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/ld+json',
                    },
                });
                showSnackbar('Оборудването е добавено успешно', 'success');
            }

            handleCloseDialog();
            fetchEquipment();
        } catch (error: any) {
            console.error('Error saving equipment:', error);
            showSnackbar(error.message || 'Грешка при запазване на оборудването', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Сигурни ли сте, че искате да изтриете това оборудване?')) {
            return;
        }

        try {
            await axios.delete(`${API_URL}/api/equipment/${id}`, {
                withCredentials: true,
            });
            showSnackbar('Оборудването е изтрито успешно', 'success');
            fetchEquipment();
        } catch (error) {
            console.error('Error deleting equipment:', error);
            showSnackbar('Грешка при изтриване на оборудването', 'error');
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const getTypeLabel = (type: string) => {
        return equipmentTypes.find(t => t.value === type)?.label || type;
    };

    const filteredEquipment = filterGymId
        ? equipment.filter(eq => {
            const eqGymId = getGymFromEquipment(eq);
            return eqGymId === filterGymId;
          })
        : equipment;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" component="h1">
                    Управление на оборудване
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Добави оборудване
                </Button>
            </Box>

            {/* Gym filter */}
            {gyms.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Филтър по зала</InputLabel>
                        <Select
                            value={filterGymId}
                            label="Филтър по зала"
                            onChange={(e) => setFilterGymId(e.target.value as number | '')}
                        >
                            <MenuItem value="">Всички зали</MenuItem>
                            {gyms.map((gym) => (
                                <MenuItem key={gym.id} value={gym.id}>{gym.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            )}

            {fetchLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Grid container spacing={2}>
                        {filteredEquipment.map((eq) => (
                            <Grid size={{ xs: 6, sm: 4, md: 2 }} key={eq.id}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    {eq.image && (
                                        <CardMedia
                                            component="img"
                                            height="140"
                                            image={`${API_URL}${eq.image}`}
                                            alt={eq.name}
                                            sx={{ objectFit: 'cover' }}
                                        />
                                    )}
                                    {!eq.image && (
                                        <Box
                                            sx={{
                                                height: 140,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: 'grey.200',
                                            }}
                                        >
                                            <ImageIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                                        </Box>
                                    )}
                                    <CardContent sx={{ flexGrow: 1, p: 1.5, '&:last-child': { pb: 1 } }}>
                                        <Typography variant="subtitle2" component="div" noWrap>
                                            {eq.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {getTypeLabel(eq.type)}
                                        </Typography>
                                        {eq.make && (
                                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                                {eq.make}
                                            </Typography>
                                        )}
                                        {eq.initialResistance != null && eq.initialResistance !== '' && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Нач. съпр.: {parseFloat(String(eq.initialResistance))} kg
                                            </Typography>
                                        )}
                                        {getGymName(eq) && (
                                            <Chip
                                                label={getGymName(eq)}
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }}
                                            />
                                        )}
                                        {eq.muscleGroups && eq.muscleGroups.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                                {eq.muscleGroups.map((mg) => (
                                                    <Chip
                                                        key={mg}
                                                        label={getMuscleGroupLabel(mg)}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: muscleGroupColors[mg] || '#757575',
                                                            color: 'white',
                                                            fontWeight: 500,
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                        {eq.description && (
                                            <Typography variant="body2" color="text.secondary">
                                                {eq.description.length > 100
                                                    ? `${eq.description.substring(0, 100)}...`
                                                    : eq.description}
                                            </Typography>
                                        )}
                                    </CardContent>
                                    <CardActions sx={{ p: 0.5, pt: 0 }}>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleOpenDialog(eq)}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => eq.id && handleDelete(eq.id)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {filteredEquipment.length === 0 && (
                        <Box sx={{ textAlign: 'center', mt: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                Все още няма добавено оборудване
                            </Typography>
                        </Box>
                    )}
                </>
            )}

            {/* Dialog for Add/Edit */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingEquipment ? 'Редактиране на оборудване' : 'Добавяне на оборудване'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label="Име"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            fullWidth
                            required
                        />

                        <FormControl fullWidth required>
                            <InputLabel>Тип</InputLabel>
                            <Select
                                value={formData.type}
                                label="Тип"
                                onChange={(e) => handleInputChange('type', e.target.value)}
                            >
                                {equipmentTypes.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Марка"
                            value={formData.make || ''}
                            onChange={(e) => handleInputChange('make', e.target.value)}
                            fullWidth
                            inputProps={{ maxLength: 100 }}
                            placeholder="напр. Technogym, Matrix, Life Fitness"
                        />

                        {formData.type === 'machine' && (
                            <TextField
                                label="Начално съпротивление (kg)"
                                type="number"
                                value={formData.initialResistance || ''}
                                onChange={(e) => handleInputChange('initialResistance', e.target.value)}
                                fullWidth
                                inputProps={{ min: 0, max: 999.99, step: 0.5 }}
                            />
                        )}

                        <FormControl fullWidth>
                            <InputLabel>Мускулни групи</InputLabel>
                            <Select
                                multiple
                                value={formData.muscleGroups}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setFormData({
                                        ...formData,
                                        muscleGroups: typeof value === 'string' ? value.split(',') : value,
                                    });
                                }}
                                input={<OutlinedInput label="Мускулни групи" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip
                                                key={value}
                                                label={getMuscleGroupLabel(value)}
                                                size="small"
                                                sx={{
                                                    bgcolor: muscleGroupColors[value] || '#757575',
                                                    color: 'white',
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            >
                                {muscleGroupOptions.map((mg) => (
                                    <MenuItem key={mg.value} value={mg.value}>
                                        <Checkbox checked={formData.muscleGroups.indexOf(mg.value) > -1} />
                                        <ListItemText primary={mg.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {gyms.length > 0 && (
                            <FormControl fullWidth>
                                <InputLabel>Зала</InputLabel>
                                <Select
                                    value={selectedGymId}
                                    label="Зала"
                                    onChange={(e) => setSelectedGymId(e.target.value as number | '')}
                                >
                                    <MenuItem value="">Без зала</MenuItem>
                                    {gyms.map((gym) => (
                                        <MenuItem key={gym.id} value={gym.id}>{gym.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <TextField
                            label="Описание"
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                        />

                        <Box>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<ImageIcon />}
                                fullWidth
                            >
                                {imageFile ? 'Промени снимка' : 'Добави снимка'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleImageSelect}
                                />
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Максимален размер: 30MB
                            </Typography>
                        </Box>

                        {imagePreview && (
                            <Box sx={{ mt: 2 }}>
                                <img
                                    src={imagePreview.startsWith('data:') ? imagePreview : `${API_URL}${imagePreview}`}
                                    alt="Preview"
                                    style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={loading || uploading}>
                        Отказ
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={loading || uploading}
                    >
                        {uploading ? 'Качване...' : loading ? 'Запазване...' : 'Запази'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default EquipmentManager;
